import { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { Source, Layer, useMap } from "react-map-gl/mapbox"
import type { LayerProps } from "react-map-gl/mapbox"
import distance from "@turf/distance"
import midpoint from "@turf/midpoint"
import type { GeoJSONSource } from "mapbox-gl"
import type { Feature, FeatureCollection, Point } from "geojson"
import { Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"

const PRIMARY_COLOR = "#3b82f6"

export const MeasureDistance = () => {
  const [lines, setLines] = useState<Feature[]>([])
  const [currentLine, setCurrentLine] = useState<number[][] | null>(null)
  const [currentLabel, setCurrentLabel] = useState<Feature | null>(null)
  const { current: map } = useMap()
  const [toolbarContainer, setToolbarContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setToolbarContainer(document.getElementById("explore-map-toolbar"))
  }, [])

  const lineLayer: LayerProps = {
    id: "line",
    type: "line",
    paint: {
      "line-color": PRIMARY_COLOR,
      "line-width": 2,
    },
  }

  const dottedLineLayer: LayerProps = {
    id: "dotted-line",
    type: "line",
    paint: {
      "line-color": PRIMARY_COLOR,
      "line-width": 2,
      "line-dasharray": [2, 2],
    },
  }

  const labelLayer: LayerProps = {
    type: "symbol",
    layout: {
      "text-field": ["get", "label"],
      "text-size": 14,
      "text-offset": [0, 1.5],
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 2,
    },
  }

  const handleMouseMove = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!currentLine || !map) return

      const newLine = [...currentLine]
      newLine[1] = [e.lngLat.lng, e.lngLat.lat]
      setCurrentLine(newLine)

      const pointA: Point = { type: "Point", coordinates: newLine[0] }
      const pointB: Point = { type: "Point", coordinates: newLine[1] }
      const dist = distance(pointA, pointB, { units: "kilometers" })
      const mid = midpoint(pointA, pointB)

      const newLabelFeature: Feature = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: mid.geometry.coordinates,
        },
        properties: {
          label: `${(dist * 1000).toFixed(2)} m`,
        },
      }

      setCurrentLabel(newLabelFeature)

      const source = map.getSource("current-label-source") as GeoJSONSource | undefined
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [newLabelFeature],
        })
      }
    },
    [currentLine, map],
  )

  const handleClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!currentLine) {
        setCurrentLine([
          [e.lngLat.lng, e.lngLat.lat],
          [e.lngLat.lng, e.lngLat.lat],
        ])
        if (map) map.getCanvas().style.cursor = "crosshair"
      } else {
        const pointA: Point = { type: "Point", coordinates: currentLine[0] }
        const pointB: Point = { type: "Point", coordinates: currentLine[1] }
        const dist = distance(pointA, pointB, { units: "kilometers" })
        const mid = midpoint(pointA, pointB)

        const newLineFeature: Feature = {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: currentLine,
          },
          properties: {
            id: `line-${lines.length}`,
            label: `${(dist * 1000).toFixed(2)} m`,
            midpoint: mid.geometry.coordinates,
          },
        }

        setLines([...lines, newLineFeature])
        setCurrentLine(null)
        setCurrentLabel(null)
        if (map) map.getCanvas().style.cursor = "not-allowed"
      }
    },
    [currentLine, lines, map],
  )

  const handleReset = useCallback(() => {
    setLines([])
    setCurrentLine(null)
    setCurrentLabel(null)
    if (map) map.getCanvas().style.cursor = "crosshair"
  }, [map])

  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      handleReset()
    },
    [handleReset],
  )

  useEffect(() => {
    if (!map) return

    const canvas = map.getCanvas()
    canvas.style.cursor = "crosshair"
    map.on("click", handleClick)
    if (currentLine) {
      map.on("mousemove", handleMouseMove)
    }
    canvas.addEventListener("contextmenu", handleContextMenu)

    return () => {
      map.off("click", handleClick)
      map.off("mousemove", handleMouseMove)
      canvas.removeEventListener("contextmenu", handleContextMenu)
      canvas.style.cursor = ""
    }
  }, [map, currentLine, handleClick, handleMouseMove, handleContextMenu])

  const geojson: FeatureCollection = {
    type: "FeatureCollection",
    features: lines,
  }

  const currentLineGeojson: FeatureCollection = currentLine
    ? {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: currentLine,
            },
            properties: {
              id: "dotted-line",
            },
          },
        ],
      }
    : { type: "FeatureCollection", features: [] }

  const currentLabelGeojson: FeatureCollection = currentLabel
    ? {
        type: "FeatureCollection",
        features: [currentLabel],
      }
    : { type: "FeatureCollection", features: [] }

  const labelGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: lines.map((line) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: line.properties?.midpoint,
      },
      properties: {
        id: `label-${line.properties?.id}`,
        label: line.properties?.label,
      },
    })),
  }

  return (
    <>
      <Source id="measure-source" type="geojson" data={geojson}>
        <Layer {...lineLayer} />
      </Source>
      <Source id="dotted-line-source" type="geojson" data={currentLineGeojson}>
        <Layer {...dottedLineLayer} />
      </Source>
      <Source id="label-source" type="geojson" data={labelGeojson}>
        <Layer id="line-label" {...labelLayer} />
      </Source>
      <Source id="current-label-source" type="geojson" data={currentLabelGeojson}>
        <Layer id="current-label" {...labelLayer} />
      </Source>
      {lines.length > 0 &&
        toolbarContainer &&
        createPortal(
          <Button variant="destructive" size="lg" onClick={handleReset}>
            <Trash2 className="h-4 w-4 mr-2" />
            Effacer
          </Button>,
          toolbarContainer,
        )}
    </>
  )
}
